import { observable } from 'mobx';
import { inject, observer } from 'mobx-react';
import React from 'react';
import type Mediator from '../register/stores/mediator';

interface IHandler<T> {
  handler: (event: T) => void;
  type: { new (): T };
}

interface IMultiHandler<T> {
  handlers: IHandler<T>[];
}

function isIMultiHandler<T>(arg: any): arg is IMultiHandler<T> {
  return arg.hasOwnProperty && arg.hasOwnProperty('handlers');
}

function isIHandle<T>(arg: any): arg is IHandler<T> {
  return arg.hasOwnProperty && arg.hasOwnProperty('handler') && arg.hasOwnProperty('type');
}

type Props<T> = { mediator?: Mediator } & (IHandler<T> | IMultiHandler<T>);

function getHandlers<T>(props: Props<T>) {
  let _handlers: IHandler<T>[] = [];

  if (isIMultiHandler<T>(props)) {
    const { handlers } = props;
    _handlers.push(...handlers);
  }

  if (isIHandle<T>(props)) {
    const { handler, type } = props;
    _handlers.push({ handler, type });
  }

  return _handlers;
}

@inject('mediator')
@observer
class Subscribe<T> extends React.Component<Props<T>> {
  get mediator() {
    return this.props.mediator!;
  }

  constructor(props: Props<T>) {
    super(props);
    const { mediator } = this;

    getHandlers<T>(props).forEach((x) => {
      return mediator.subscribe<T>(x.type, x.handler);
    });
  }

  render() {
    return <>{this.props.children}</>;
  }

  componentWillUnmount() {
    let { mediator } = this;

    getHandlers<T>(this.props).forEach((x) => {
      return mediator.unsubscribe<T>(x.type, x.handler);
    });
  }
}

export default Subscribe;
